/**
 * Fetch Contractor Categories API
 *
 * API function for fetching categories assigned to a specific contractor
 */

import { buildApiUrl, handleApiResponse } from '@/src/shared/api';

/**
 * Category info nested within contractor category assignment
 */
export interface ContractorCategoryInfo {
  id: string;
  nameEn: string;
  nameFr: string;
  slug: string;
  icon: string | null;
}

/**
 * Contractor category assignment with category details
 */
export interface ContractorCategory {
  id: string;
  categoryId: string;
  category: ContractorCategoryInfo;
}

/**
 * Contractor profile response (partial - only what we need)
 */
interface ContractorProfileResponse {
  id: string;
  categories?: ContractorCategory[];
}

/**
 * Fetch categories assigned to a specific contractor
 *
 * Uses the public GET /contractors/:id endpoint which returns
 * contractor profile including their assigned categories.
 *
 * @param contractorId - The contractor ID or user ID
 * @returns Promise with array of contractor categories
 */
export async function fetchContractorCategories(
  contractorId: string
): Promise<ContractorCategory[]> {
  const url = buildApiUrl(`/contractors/${contractorId}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const profile = await handleApiResponse<ContractorProfileResponse>(response);

  return profile.categories || [];
}
